import React from 'react';
import { Typography } from '@mui/material';
import MessageContent, { type MessageContentProps } from '@sendbird/uikit-react/ui/MessageContent';

import { parseJSON } from 'lib/storage';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import LinkMui from '@mui/material/Link';
import { useDispatch, useSelector } from 'react-redux';
import { userProfileSelector } from 'domain/env';
import { companiesByIdSelector } from 'domain/companies';
import { setTargetMessageAction } from 'domain/chat';

type Props = MessageContentProps;

const Message: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const userProfile = useSelector(userProfileSelector);
  const companies = useSelector(companiesByIdSelector);

  const isByMe = 'sender' in props.message && props.message.sender?.userId === userProfile.id;
  const metadata = props?.message?.data ? parseJSON(props?.message?.data) : '';

  // DA-19505: The Welcome message lives in a frozen channel — in DOKKA the only
  // frozen channel is the Welcome message channel (see styles.css frozen-banner
  // note). No message action is meaningful there, so suppress the reaction picker,
  // the message menu (Reply / Copy) and the mobile long-press menu.
  const isFrozenChannel = Boolean(props?.channel?.isFrozen);

  const handleClickOnContext = () => {
    dispatch(setTargetMessageAction({ id: props?.message?.messageId, timestamp: props?.message?.createdAt }));
  };

  const elements = [];
  if (metadata?.app) {
    elements.push(
      <Typography key="app" variant="caption" sx={{ color: 'text.secondary' }}>
        {metadata.app}
      </Typography>,
    );
  }
  if (metadata?.companyId) {
    const companyPath = generatePath(ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.absolute, {
      companyId: metadata.companyId,
    });
    elements.push(
      <LinkMui
        key="company"
        component={RouterLink}
        to={companyPath}
        onClick={handleClickOnContext}
        variant="caption"
        sx={{
          color: 'text.secondary',
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline', color: 'primary.main' },
        }}
      >
        {companies.get(metadata.companyId)?.cname || metadata.companyId}
      </LinkMui>,
    );
  }
  if (metadata?.documentId) {
    const documentPath = metadata?.companyId
      ? generatePath(ROUTES_PATH.DOCUMENT.absolute, { companyId: metadata.companyId, documentId: metadata.documentId })
      : null;
    const docId = String(metadata.documentId);

    const shortenedDocId = docId.length > 10 ? `...${docId.slice(-10)}` : docId;
    elements.push(
      documentPath ? (
        <LinkMui
          key="doc"
          component={RouterLink}
          to={documentPath}
          onClick={handleClickOnContext}
          variant="caption"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline', color: 'primary.main' },
          }}
        >
          {shortenedDocId}
        </LinkMui>
      ) : (
        <Typography key="doc" variant="caption" sx={{ color: 'text.secondary' }}>
          {shortenedDocId}
        </Typography>
      ),
    );
  }

  return (
    <div className="custom-message-wrapper" style={{ display: 'flex', flexDirection: 'column' }}>
      <MessageContent
        {...props}
        {...(isFrozenChannel
          ? {
              isReactionEnabled: false,
              renderMessageMenu: () => null,
              renderMobileMenuOnLongPress: () => <></>,
            }
          : {})}
      />
      <div
        className="custom-metadata"
        style={{
          fontSize: '11px',
          color: 'gray',
          marginTop: '4px',

          alignSelf: isByMe ? 'flex-end' : 'flex-start',
          padding: '0 12px',
        }}
      >
        {elements.reduce((prev, curr, i) => (i === 0 ? [curr] : [...prev, ' > ', curr]), [])}
      </div>
    </div>
  );
};

export default Message;
